import * as Rx from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Collections } from '../collections.model';
import {
  assertNever,
  fromPairs,
  isDefined,
  isEqual,
  jsonStableStringify,
  partition,
  sameElements,
  setDiff,
  toObservable,
} from './helpers';
import { OrderByDirection, Ordering, Query } from './query';
import {
  ArrayElement,
  Entities,
  Entity,
  EntityState,
  List,
  Lists,
  Path,
  QueryParam,
  QueryParams,
  RawQuery,
  Reference,
  StoreFeatureState,
  StoreState,
} from './store.model';

// If an entity matches a query. If there's no query then it does so trivially.
function entityMatchesQueryParams<T>(
  entity: Entity<T>,
  params?: QueryParams<T>,
) {
  return params
    ? // TODO: QueryParams entries are too complicated for TypeScript to
      // understand that it suddenly just assumes {}
      Object.values<any>(params).every((param: QueryParam<T>) => {
        if (param.name in entity.entity) {
          const entityValue = entity.entity[param.name];

          switch (param.condition) {
            case '==':
              return isEqual(entityValue, param.value);
            case 'in':
              return param.values.some(value => isEqual(entityValue, value));
            case 'equalsIgnoreCase':
              return (
                param.value.toUpperCase() === String(entityValue).toUpperCase()
              );
            case 'includes':
              return ((entityValue as any) as ArrayElement<T[keyof T]>[]).some(
                value => isEqual(param.value, value),
              );
            default:
              return assertNever(param);
          }
        }

        return false;
      })
    : true;
}

export function stringifyQuery<T>(query: RawQuery<T> | undefined) {
  if (query) {
    interface KeyConditionValue {
      key: string;
      condition: string;
      value: any;
    }

    const { params, ...pagination } = query;

    const stringifiedParams: KeyConditionValue[] = params
      ? Object.entries(params).map<KeyConditionValue>(
          ([key, param]: [string, QueryParam<T>]) => {
            // When stringifying pick something that can be concatenated without ambiguity
            switch (param.condition) {
              case '==':
                return { key, condition: param.condition, value: param.value };
              case 'equalsIgnoreCase':
                return { key, condition: '~ignoreCase~', value: param.value };
              case 'includes':
                return { key, condition: '~includes~', value: param.value };
              case 'in':
                return { key, condition: '~in~', value: param.values };
              default:
                return assertNever(param);
            }
          },
        )
      : [];

    const entries = Object.entries(pagination)
      .map(
        ([key, value]) =>
          ({ key, condition: '==', value } as KeyConditionValue),
      )
      .filter(({ key, condition, value }) => value !== undefined)
      .concat(stringifiedParams)
      .map(
        ({ key, condition, value }) =>
          `${key}${condition}${jsonStableStringify(value, true)}`,
      )
      .sort()
      .join(';');

    return entries || 'default';
  }

  return 'default';
}

export function flattenQuery<K extends keyof Collections>(
  queryFn?: (q: Query<K>) => Query<K> | Rx.Observable<Query<K>>,
): Rx.Observable<{
  readonly limit: number | undefined;
  readonly params: QueryParams<Collections[K]>;
  readonly order: Ordering<K> | undefined;
}> {
  const rawQuery$ = queryFn
    ? toObservable(queryFn(Query.emptyQuery())).pipe(map(q => q.query))
    : Rx.of({
        limit$: Rx.of(undefined),
        params$: Rx.of({}),
        order$: Rx.of(undefined),
      });

  return rawQuery$.pipe(
    switchMap(query =>
      Rx.combineLatest(query.limit$, query.params$, query.order$).pipe(
        map(([limit, params, order]) => ({ limit, params, order })),
      ),
    ),
    distinctUntilChanged((x, y) => stringifyQuery(x) === stringifyQuery(y)),
  );
}

function compare<T>(a: T, b: T, direction = OrderByDirection.ASC) {
  const comp = (x: T, y: T) => (x < y ? -1 : x > y ? 1 : 0);

  switch (direction) {
    case OrderByDirection.ASC:
      return comp(a, b);
    case OrderByDirection.DESC:
      return comp(b, a);
    default:
      return assertNever(direction);
  }
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  entities: Entities<Collections[K]>,
  order: Ordering<K>,
  ref: Reference<K>,
): StoreFeatureState<K> {
  const data =
    state[ref.path.authUid] && state[ref.path.authUid].entities
      ? state[ref.path.authUid]
      : { entities: {}, lists: {} };
  const queryString = stringifyQuery(ref.query);

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  const newListIds = Object.keys(entities)
    .filter(key => {
      const matches = entityMatchesQueryParams(
        entities[key],
        ref.query ? ref.query.params : undefined,
      );
      if (!matches) {
        console.error(
          'Attempted to merge invalid entity into list:',
          ref.path.collection,
          ref.query,
          entities[key],
        );
      }
      return matches;
    })
    .sort((id1, id2) =>
      compare(
        updatedEntities[id1].entity[order.field],
        updatedEntities[id2].entity[order.field],
        order.direction,
      ),
    );

  const query: RawQuery<Collections[K]> = ref.query || { params: {} };

  const otherListsUpdated: Lists<K> = fromPairs(
    Object.entries(data.lists)
      .filter(([key]) => key !== queryString)
      .map(
        ([key, list]) =>
          [key, updateList(list, entities, updatedEntities)] as [
            string,
            List<K>
          ],
      ),
  );

  return {
    ...state,
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: {
        ...otherListsUpdated,
        [queryString]: { ids: newListIds, query, order },
      },
    },
  };
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 * TODO T40096: This is a websocket version of mergeEntities, which has more
 * logic right now to apply the websocket data on the right place of the store
 * slice. Maybe we can merge this with mergeEntities again?
 */
export function mergeWebsocketEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  entities: Entities<Collections[K]>,
  ref: Reference<K>,
): StoreFeatureState<K> {
  const data: EntityState<K> =
    state[ref.path.authUid] && state[ref.path.authUid].entities
      ? state[ref.path.authUid]
      : { entities: {}, lists: {} };

  const updatedEntities = { ...data.entities, ...entities };

  if (!data.lists) {
    return state;
  }

  const updatedLists: Lists<K> = fromPairs(
    Object.entries(data.lists).map(
      ([key, list]) =>
        [key, updateList(list, entities, updatedEntities)] as [string, List<K>],
    ),
  );

  return {
    ...state,
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: { ...data.lists, ...updatedLists },
    },
  };
}

function updateList<K extends keyof Collections>(
  list: List<K>,
  entities: Entities<Collections[K]>,
  updatedEntities: Entities<Collections[K]>,
): List<K> {
  const { query, order } = list;
  const { params, offset, limit } = query;

  // If the offset is non-zero we don't add it to the new list.
  if (offset) {
    return list;
  }

  // Existing entity ids of the old list
  const currEntityIds = list ? Array.from(list.ids) : [];

  // Find which entities modified by the websocket now match (or don't match)
  // the query. These should be added (or removed) from the list
  const [matchingIds, nonMatchingIds] = partition(
    Object.entries(entities),
    ([entityId, entity]) => entityMatchesQueryParams(entity, params),
  ).map(entityEntries => entityEntries.map(([entityId]) => entityId));

  // The new list is the old list plus any matching entity ids, minus any
  // non-matching entity ids, while ensuring all ids are unique
  const filteredEntityIds = setDiff(
    currEntityIds.concat(matchingIds),
    nonMatchingIds,
  );

  // Finally, apply limit to the ids before merging into list
  const updatedEntityIds = [...filteredEntityIds]
    .sort((id1, id2) =>
      compare(
        updatedEntities[id1].entity[order.field],
        updatedEntities[id2].entity[order.field],
        order.direction,
      ),
    )
    .slice(0, limit);

  return { ...list, ids: updatedEntityIds };
}

/**
 * Add/overwrite a list of entities to the datastore that are from a websocket.
 * If you instead want to modify an existing object use
 * `transformWebsocketEntities`.]
 * If your transformer maps to `undefined` then the entity will be ignored.
 */
export function addWebsocketEntities<K extends keyof Collections, O>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O) => Collections[K] | undefined,
  ref: Reference<K>,
): StoreFeatureState<K>;
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O, extra: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra: E,
): StoreFeatureState<K>;
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<O>,
  transform: (a: O, extra?: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra?: E,
): StoreFeatureState<K> {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects(list, transform, extra),
    ref,
  );
}

/**
 * Take what is in the datastore and run a transformation on it.
 * If an item is not found in the store then it is ignored.
 */
export function updateWebsocketEntities<K extends keyof Collections>(
  state: StoreFeatureState<K>,
  list: ReadonlyArray<string> | ReadonlyArray<number>,
  transform: (a: Collections[K]) => Collections[K],
  ref: Reference<K>,
): StoreFeatureState<K> {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects<Collections[K], string | number>(
      list,
      id => {
        const object = pluckItemFromRawStoreSlice(state, ref.path, id);
        return object ? transform(object) : undefined;
      },
    ),
    ref,
  );
}

interface ObjectWithId {
  readonly id: number | string;
}

export function transformObjects<T extends ObjectWithId, O>(
  list: ReadonlyArray<O> | { readonly [id: number]: O } | undefined,
  transform: (a: NonNullable<O>) => T,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O> | { readonly [id: number]: O } | undefined,
  transform: (a: NonNullable<O>, extra: E) => T,
  extra: E,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O> | { readonly [id: number]: O } | undefined,
  transform: (a: NonNullable<O>, extra?: E) => T,
  extra?: E,
): Entities<T> {
  if (!list) {
    return {};
  }

  const actualList = Array.isArray(list) ? list : Object.values<O>(list);

  return actualList
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {}); // Switch to fromPairs.
}

/** This should only be used for websocket transformers as we shouldn't skip
 * objects when transforming get requests or it will break pagination.
 * It should not be exported.
 */
function transformOptionalWebsocketObjects<T extends ObjectWithId, O>(
  list: ReadonlyArray<O>,
  transform: (a: O) => T | undefined,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O>,
  transform: (a: O, extra: E) => T | undefined,
  extra: E,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: ReadonlyArray<O>,
  transform: (a: O, extra?: E) => T | undefined,
  extra?: E,
): Entities<T> {
  return list
    .map(o => transform(o))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {});
}

export function pathsEqual<K extends keyof Collections>(
  a: Path<K>,
  b: Path<K>,
) {
  return (
    a.collection === b.collection &&
    a.authUid === b.authUid &&
    sameElements(a.ids || [], b.ids || [])
  );
}

/**
 * Check if both QueryParams are equal down to the QueryParam values.
 * @param a QueryParams to compare.
 * @param b Another QueryParams to compare.
 * @return boolean Whether or not it's the same.
 */
function rawQueryEqual<T>(a: RawQuery<T>, b: RawQuery<T>) {
  const aQuery = stringifyQuery(a);
  const bQuery = stringifyQuery(b);

  return aQuery === bQuery;
}

export function referencesEqual<K extends keyof Collections>(
  a: Reference<K>,
  b: Reference<K>,
) {
  return (
    pathsEqual(a.path, b.path) &&
    ((a.query === undefined && b.query === undefined) ||
      (a.query !== undefined &&
        b.query !== undefined &&
        a.query.offset === b.query.offset &&
        a.query.limit === b.query.limit &&
        rawQueryEqual(a.query, b.query)))
  );
}

export function pluckItemFromRawStore<K extends keyof Collections>(
  store: StoreState,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  return store[path.collection][path.authUid] &&
    store[path.collection][path.authUid].entities[id]
    ? store[path.collection][path.authUid].entities[id].entity
    : undefined;
}

export function pluckItemFromRawStoreSlice<K extends keyof Collections>(
  storeSlice: StoreFeatureState<K>,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  if (!storeSlice[path.authUid] || !storeSlice[path.authUid].entities[id]) {
    return undefined;
  }
  return storeSlice[path.authUid].entities[id].entity;
}

export function removeEntityById<K extends keyof Collections>(
  ref: Reference<K>,
  state: StoreFeatureState<K>,
  id: string | number,
): StoreFeatureState<K> {
  const data = state[ref.path.authUid] || { lists: {}, entities: {} };
  const entities = { ...data.entities };
  delete entities[id];

  // Remove the deleted ID from any list
  const lists = Object.entries(data.lists).reduce(
    (obj, [queryString, list]) => {
      const newIds = (list && list.ids ? list.ids : []).filter(
        listId => listId.toString() !== id.toString(),
      );

      return { ...obj, [queryString]: { ...list, ids: newIds } };
    },
    {} as Lists<K>,
  );

  return { ...state, [ref.path.authUid]: { entities, lists } };
}

/**
 * Gets the query param value of a specific query param name and returns it in
 * an array.
 * @param query The query.
 * @param name The key of the entity for the query.
 * @returns An array of the values.
 */
export function getQueryParamValue<T, K extends keyof T>(
  query: RawQuery<T> | undefined,
  name: K,
): ReadonlyArray<T[K]> {
  if (!query || !query.params) {
    return [];
  }

  const param = query.params[name];
  if (!param) {
    return [];
  }

  switch (param.condition) {
    case '==':
    case 'equalsIgnoreCase': {
      return [param.value];
    }

    case 'in': {
      return param.values;
    }
    case 'includes': {
      return [[param.value]] as any; // ArrayElement<T[K]>[] is mostly T[K]
    }

    default: {
      return [];
    }
  }
}
